/**
 * Цели бюджета сезона (ADR-069): УК ставит целевые числа по выручке и прибыли
 * на трёх уровнях — холдинг / кластеры / хозяйства («каждый получает свой
 * план», founder 19.08). Хозяйство свою цель читает и подгоняет под неё
 * варианты; сводка варианта (#803) сверяет факт против цели светофором.
 *
 * Язык — экономиста и агронома: «цель по выручке», «цель по прибыли»,
 * «расхождение с суммой по хозяйствам». Модельные слова (target, verdict,
 * consistency) наружу не отдаём.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { describeApiError } from '@/lib/apiError';
import { planVariantKeys } from '@/lib/planning/queryKeys';
import { toast } from '@/lib/toast';

// ── Типы ответа ──────────────────────────────────────────────────────────────

export interface BudgetTarget {
  target_revenue: number | null;
  target_margin: number | null;
  target_margin_pct: number | null;
  source_note: string | null;
  set_at: string | null;
}

/** ok | partial | mismatch — диагностика декомпозиции, не валидация. */
export interface TargetConsistency {
  status: 'ok' | 'partial' | 'mismatch';
  children_sum_revenue: number | null;
  delta: number | null;
}

export interface TargetsTreeFarm {
  id: string;
  name: string;
  cluster_id: string | null;
  target: BudgetTarget | null;
}

export interface TargetsTreeCluster {
  id: string;
  name: string;
  target: BudgetTarget | null;
  consistency: TargetConsistency;
  farms: TargetsTreeFarm[];
}

export interface TargetsTree {
  season_id: string;
  company_id: string;
  company_name: string;
  company_target: BudgetTarget | null;
  consistency: TargetConsistency;
  clusters: TargetsTreeCluster[];
  /** Хозяйства без кластера — прямые дети холдинга в декомпозиции. */
  farms: TargetsTreeFarm[];
  money_visible: boolean;
  can_edit: boolean;
}

export type TargetLevel = 'company' | 'cluster' | 'farm';

export interface TargetPutBody {
  level: TargetLevel;
  object_id: string | null;
  target_revenue: number | null;
  target_margin: number | null;
  target_margin_pct: number | null;
  source_note: string | null;
}

/** Светофор сводки варианта — блок `targets` ответа `GET …/summary`. */
export type TargetVerdict = 'hit' | 'near' | 'miss' | 'not_set' | 'not_comparable';

export interface TargetMetric {
  target: number | null;
  fact: number | null;
  ratio: number | null;
  verdict: TargetVerdict;
  inherited_from?: 'farm' | 'cluster' | 'company' | null;
}

export interface VariantTargets {
  revenue: TargetMetric;
  margin: TargetMetric;
  margin_pct: TargetMetric;
  comparable: boolean;
  comparable_reason: 'coverage_incomplete' | null;
  higher_level_revenue: boolean;
}

// ── Ключи и запросы ──────────────────────────────────────────────────────────

export const budgetTargetKeys = {
  all: () => ['planning', 'budget-targets'] as const,
  tree: (seasonId: string | null) => ['planning', 'budget-targets', 'tree', seasonId ?? 'none'] as const,
};

export function useTargetsTree(seasonId: string | null, enabled = true) {
  return useQuery<TargetsTree, Error>({
    queryKey: budgetTargetKeys.tree(seasonId),
    queryFn: () => api.get<TargetsTree>(`/api/v1/planning/seasons/${seasonId}/budget-targets/tree`),
    enabled: enabled && !!seasonId,
    staleTime: 15_000,
  });
}

export function useSaveTarget(seasonId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ consistency: TargetConsistency }, Error, TargetPutBody>({
    mutationFn: (body) => api.put(`/api/v1/planning/seasons/${seasonId}/budget-targets`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: budgetTargetKeys.all() });
      // Светофор сводки варианта считается против этих целей — обязан протухнуть.
      void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
      toast.success('Цель сохранена');
    },
    // Причина отказа — словами сервера («Цель этого уровня ставится уровнем
    // выше», «Нет доступа к денежным показателям бюджета», сетевой сбой…).
    // Гадать «проверьте права» вместо реальной причины — «пусто ≠ мусор»:
    // при 500 это враньё, при 403 — недосказанность (UAT founder 20.08).
    onError: (err) => toast.error(`Не удалось сохранить цель: ${describeApiError(err)}`),
  });
}

// ── Подписи и подсказки (язык экономиста) ────────────────────────────────────

export const VERDICT_TITLES: Record<TargetVerdict, string> = {
  hit: 'цель достигается',
  near: 'почти у цели',
  miss: 'ниже цели',
  not_set: 'цель не задана',
  not_comparable: 'сверка невозможна',
};

/** Вариант Badge design system под каждый вердикт — новых цветов не заводим. */
export const VERDICT_BADGE: Record<TargetVerdict, 'primary' | 'accent-brand' | 'destructive' | 'secondary'> = {
  hit: 'primary',
  near: 'accent-brand',
  miss: 'destructive',
  not_set: 'secondary',
  not_comparable: 'secondary',
};

/**
 * Подсказка расхождения декомпозиции: «Σ по хозяйствам ≠ цель кластера».
 * Warning, не блокер — УК вправе держать резерв или ставить растяжку.
 */
export function consistencyHint(c: TargetConsistency | undefined, childrenWord: string): string | null {
  if (!c || c.status === 'ok' || c.delta === null) return null;
  const dir = c.delta > 0 ? 'больше' : 'меньше';
  const partial = c.status === 'partial' ? ' (не у всех цели заданы — сумма неполная)' : '';
  return `Сумма целей по ${childrenWord} ${dir} на ${formatTargetMln(Math.abs(c.delta))}${partial}`;
}

/** Деньги целей — в миллионах, как остальной план. */
export function formatTargetMln(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (Math.abs(value) < 1_000_000) {
    return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(value))} ₽`;
  }
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value / 1_000_000)} млн ₽`;
}

/**
 * Разбор черновика инпута цели. Валидирующий, а не «молчаливый»: мусор в поле
 * НЕ превращается в null — иначе «abc» сохранялся бы как «цель не задана» с
 * зелёным тостом (блокер head-web на #828). Приём — YieldCell из #817:
 * невалидное остаётся в поле с ошибкой, пусто = осознанная очистка.
 */
export type DraftParse = { ok: true; value: number | null } | { ok: false; error: string };

/** «₽» из инпута: пробелы и запятая поддержаны («120 000 000», «1,5»). */
export function parseMoneyDraft(raw: string, opts: { allowNegative?: boolean } = {}): DraftParse {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return { ok: true, value: null }; // пусто = осознанная очистка цели
  const num = Number(cleaned);
  if (!Number.isFinite(num)) {
    return { ok: false, error: 'Нужно число в рублях, например 1 200 000 000' };
  }
  if (num < 0 && !opts.allowNegative) {
    return { ok: false, error: 'Цель по выручке не бывает отрицательной' };
  }
  return { ok: true, value: num };
}

/** «%» из инпута: отрицательный допустим (плановый убыточный год). */
export function parsePctDraft(raw: string): DraftParse {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return { ok: true, value: null };
  const num = Number(cleaned);
  if (!Number.isFinite(num)) {
    return { ok: false, error: 'Нужен процент числом, например 34 или 12,5' };
  }
  // Границы бэкенда (TargetPut: -100…1000) — отбиваем здесь словами, а не 422.
  if (num < -100 || num > 1000) {
    return { ok: false, error: 'Процент вне разумного диапазона (от −100 до 1000)' };
  }
  return { ok: true, value: num };
}
